import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { investorService } from '../../../services/investor.service';
import { toast } from 'react-toastify';
import '../IntroducedRegistration/IntroducedInvestorRegistration.scss';
import { CompactHeader } from '../../../components/CompactHeader/CompactHeader';
import { CompactFooter } from '../../../components/CompactFooter/CompactFooter';
import { RegistrationStepper } from '../../../components/RegistrationStepper/RegistrationStepper';
import { RegistrationVisualCard } from '../../../components/RegistrationVisualCard/RegistrationVisualCard';
import { SELF_STEPS } from './selfSteps';

/**
 * Step 3: Email Entry
 * User enters email and goes straight to the details step (no email OTP: removed at the client's request)
 */
export const SelfRegistrationStep1: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { email: initialEmail, consentGiven } = location.state || {};

  // Self-registration is Individual only: the Individual / Legal Entity choice is hidden at the client's request.
  const registerAs = 1;
  const [email, setEmail] = useState<string>(initialEmail || '');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!email || !email.trim()) {
      setErrors({ email: 'Email is required.' });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setErrors({ email: 'Please enter a valid email address.' });
      return;
    }

    setLoading(true);
    try {
      // Consent was given on the previous page; the API records it with the session.
      const response = await investorService.submitEmail({
        email: email.trim(),
        registerAs,
        consentGiven: consentGiven === true,
      });

      if (!response.success) {
        toast.error(response.message || 'Registration failed. Please try again.');
        return;
      }

      toast.success(response.message);
      navigate('/investor/register/step3', {
        state: {
          uniqueCode: response.uniqueCode,
          email: email.trim(),
          registerAs,
          consentGiven
        }
      });
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CompactHeader />
      <section className="login-form-style4 steps4-sec section-padding align-items-center">
        <div className="container">
          <div className="registration-split-layout">
            {/* Left Panel: Form & Stepper */}
            <div className="left-panel">
              {/* Stepper Progress */}
              <RegistrationStepper currentStep={3} title="Email" maxWidth="550px" steps={SELF_STEPS} />

              {/* Investor Registration Card */}
              <div className="login-form-style3-main" style={{ width: '100%', maxWidth: '550px', margin: '0 auto' }}>
                <div className="login-form-style3-main_full">
                  <div className="login-register3-form-middle" style={{ textAlign: 'left' }}>
                    <form onSubmit={handleEmailSubmit} noValidate>
                      {/* Email Input */}
                      <div className="single-field">
                        <label htmlFor="email" className="w-100 mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>
                          Please enter email address here <span className="star-color">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          className="form-control"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email"
                          required
                        />
                        {errors.email && <span role="alert">{errors.email}</span>}
                      </div>

                      {/* Submit / Action Buttons */}

                      <div className="flex gap-2 justify-center single-field mb-0 mt-3 border-t pt-3">
                        <button
                          className="btn btn-primary"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? 'Processing...' : 'Continue'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => navigate('/investor/register/consent', { state: { email, registerAs } })}
                          disabled={loading}
                        >
                          Back
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel: Creative Illustration Panel */}
            <div className="right-panel">
              <RegistrationVisualCard step="email" />
            </div>
          </div>
        </div>
      </section>
      <CompactFooter />
    </>
  );
};

export default SelfRegistrationStep1;
